import { MongoObjectIdSchema } from '@terra/shared-utils';
import { TargetTypeSchema } from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import { INTEGRATION_FLOWS } from './integration-flows';
import { TicketingProviderSettingsSchema } from './ticketing-provider-settings.schema';

export const INTEGRATION_CONFIGURATION_PROFILE_KINDS = {
  DEFAULT: 'default',
  CUSTOM: 'custom',
} as const;

export const IntegrationConfigurationProfileKindSchema = z.enum([
  INTEGRATION_CONFIGURATION_PROFILE_KINDS.DEFAULT,
  INTEGRATION_CONFIGURATION_PROFILE_KINDS.CUSTOM,
]);

export type IntegrationConfigurationProfileKind = z.infer<
  typeof IntegrationConfigurationProfileKindSchema
>;

export const DEFAULT_CONFIGURATION_PROFILE_NAME = 'Default';

const ProfileIntegrationFlowSchema = z.enum([
  INTEGRATION_FLOWS.CREATE_TICKET_ON_FINDING,
  INTEGRATION_FLOWS.SYNC_STATUS_RETEST,
  INTEGRATION_FLOWS.SYNC_STATUS_FALSE_POSITIVE,
  INTEGRATION_FLOWS.SYNC_STATUS_WONTFIX,
  INTEGRATION_FLOWS.NOTIFY_ON_REMEDIATION_PR,
]);

const ProfileFieldMappingSchema = z.object({
  terraField: z.string(),
  providerField: z.string(),
});

export const IntegrationConfigurationProfileSettingsSchema =
  TicketingProviderSettingsSchema;

export type IntegrationConfigurationProfileSettings = z.infer<
  typeof IntegrationConfigurationProfileSettingsSchema
>;

export const IntegrationConfigurationProfileDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: IntegrationConfigurationProfileKindSchema,
  targetType: TargetTypeSchema.nullable(),
  targetIds: z.array(z.string()),
  fieldMappings: z.array(ProfileFieldMappingSchema),
  enabledFlows: z.array(ProfileIntegrationFlowSchema),
  providerSettings: IntegrationConfigurationProfileSettingsSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type IntegrationConfigurationProfileDto = z.infer<
  typeof IntegrationConfigurationProfileDtoSchema
>;

export const IntegrationConfigurationProfileListResponseSchema = z.object({
  profiles: z.array(IntegrationConfigurationProfileDtoSchema),
});

export const IntegrationConfigurationProfileSampleIssueResponseSchema =
  z.object({
    issueKey: z.string(),
    issueUrl: z.string(),
  });

export type IntegrationConfigurationProfileSampleIssueResponse = z.infer<
  typeof IntegrationConfigurationProfileSampleIssueResponseSchema
>;

export const IntegrationConfigurationProfileIdParamSchema = z.object({
  profileId: MongoObjectIdSchema,
});

const CustomConfigurationProfileNameSchema = z
  .string()
  .trim()
  .min(1, 'Configuration name is required')
  .max(40, 'Configuration name must be at most 40 characters')
  .refine(
    (value) =>
      /^[\p{L}\p{N}](?:[\p{L}\p{N}\s\-_/&]*[\p{L}\p{N}])?$/u.test(value),
    'Configuration name contains invalid characters',
  )
  .refine(
    (value) =>
      value.toLowerCase() !== DEFAULT_CONFIGURATION_PROFILE_NAME.toLowerCase(),
    'Configuration name cannot be Default',
  );

const ConfigurationProfileUpdateNameSchema = z
  .string()
  .trim()
  .min(1, 'Configuration name is required')
  .max(40, 'Configuration name must be at most 40 characters')
  .refine(
    (value) =>
      /^[\p{L}\p{N}](?:[\p{L}\p{N}\s\-_/&]*[\p{L}\p{N}])?$/u.test(value),
    'Configuration name contains invalid characters',
  );

const ConfigurationProfileSettingsBodySchema = z.object({
  fieldMappings: z.array(ProfileFieldMappingSchema),
  enabledFlows: z.array(ProfileIntegrationFlowSchema),
  providerSettings: IntegrationConfigurationProfileSettingsSchema.optional(),
});

export const CreateIntegrationConfigurationProfileBodySchema =
  ConfigurationProfileSettingsBodySchema.extend({
    kind: IntegrationConfigurationProfileKindSchema,
    name: z.string().optional(),
    targetType: TargetTypeSchema.optional(),
    targetIds: z.array(MongoObjectIdSchema).optional(),
  }).superRefine((body, ctx) => {
    if (body.kind === INTEGRATION_CONFIGURATION_PROFILE_KINDS.CUSTOM) {
      const parsedName = CustomConfigurationProfileNameSchema.safeParse(
        body.name,
      );

      if (!parsedName.success) {
        for (const issue of parsedName.error.issues) {
          ctx.addIssue({ ...issue, path: ['name'] });
        }
      }

      if (!body.targetType) {
        ctx.addIssue({
          code: 'custom',
          message: 'Custom configuration must include a target type',
          path: ['targetType'],
        });
      }

      if (!body.targetIds || body.targetIds.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Custom configuration must include at least one target',
          path: ['targetIds'],
        });
      }

      if (
        body.targetType &&
        body.targetType !== TargetTypeSchema.enum.WEB_APPLICATION
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Only WEB_APPLICATION targets are supported in v1',
          path: ['targetType'],
        });
      }
    }

    if (body.kind === INTEGRATION_CONFIGURATION_PROFILE_KINDS.DEFAULT) {
      if (body.targetType) {
        ctx.addIssue({
          code: 'custom',
          message: 'Default configuration cannot target a specific target type',
          path: ['targetType'],
        });
      }

      if (body.targetIds && body.targetIds.length > 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Default configuration cannot target specific targets',
          path: ['targetIds'],
        });
      }
    }
  });

export type CreateIntegrationConfigurationProfileBody = z.infer<
  typeof CreateIntegrationConfigurationProfileBodySchema
>;

export const UpdateIntegrationConfigurationProfileBodySchema =
  ConfigurationProfileSettingsBodySchema.extend({
    name: ConfigurationProfileUpdateNameSchema.optional(),
    targetType: TargetTypeSchema.optional(),
    targetIds: z.array(MongoObjectIdSchema).optional(),
  }).superRefine((body, ctx) => {
    if (
      body.targetType &&
      body.targetType !== TargetTypeSchema.enum.WEB_APPLICATION
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Only WEB_APPLICATION targets are supported in v1',
        path: ['targetType'],
      });
    }
  });

export type UpdateIntegrationConfigurationProfileBody = z.infer<
  typeof UpdateIntegrationConfigurationProfileBodySchema
>;

export { ProfileFieldMappingSchema, ProfileIntegrationFlowSchema };
